import React, { useEffect, useMemo, useState } from "react";
import {
    Drawer,
    Stack,
    Group,
    Button,
    Select,
    Textarea,
    Text,
    Divider,
    Alert,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";

import {
    DriverPersonReadDto,
    TransportVehicleReadDto,
    OptionTransportStatusDto,
} from "./../../../Transport/types";

import { getTransportStatusOptions, updateRequestAdmin, getDriversForEvent, deleteRequestAdmin } from "./../../../../api/transportAdminApi";
import { useTransportLocations } from "./../../../Transport/useTransportLocations";

const RED = "#ff5e5e";

type Props = {
    opened: boolean;
    onClose: () => void;
    bookingId: number | null;
    eventId: number;

    drivers: DriverPersonReadDto[];
    vehicles: TransportVehicleReadDto[];

    initial: {
        kind?: number | null;

        driverId?: number | null;
        vehicleId?: number | null;
        plannerNote?: string | null;
        statusTransportId?: number | null;

        scheduledDate?: string | null;
        scheduledTime?: string | null;

        pickUpPlace?: string | null;
        dropOffPlace?: string | null;
    };

    onSaved: () => void;
};

function isArrival(kind: any) {
    return kind === 1 || String(kind).toLowerCase() === "arrival";
}

function isDeparture(kind: any) {
    return kind === 2 || String(kind).toLowerCase() === "departure";
}

export default function AssignBookingDrawer(props: Props) {
    const { opened, onClose, bookingId, eventId, drivers, vehicles, initial, onSaved } = props;

    const [driverId, setDriverId] = useState<string | null>(null);
    const [driverOptions, setDriverOptions] = useState<DriverPersonReadDto[]>([]);
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [vehicleId, setVehicleId] = useState<string | null>(null);
    const [note, setNote] = useState("");
    const [statusId, setStatusId] = useState<string | null>(null);

    const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
    const [scheduledTime, setScheduledTime] = useState<string>("");

    const [pickUpPlace, setPickUpPlace] = useState("");
    const [dropOffPlace, setDropOffPlace] = useState("");

    const [statusOptions, setStatusOptions] = useState<OptionTransportStatusDto[]>([]);

    const [deleting, setDeleting] = useState(false);
    const [saving, setSaving] = useState(false);

    const { loading: loadingLoc, error: locError, pickupSuggestions, dropoffSuggestions } =
        useTransportLocations(String(eventId), "dk");

    useEffect(() => {
        if (!opened) return;

        const loadDrivers = async () => {
            try {
                setLoadingDrivers(true);

                const dateStr =
                    scheduledDate
                        ? scheduledDate.toISOString().slice(0, 10)
                        : (initial.scheduledDate ?? "").slice(0, 10) || null;

                const data = await getDriversForEvent(eventId, dateStr);
                setDriverOptions(data);
            } catch {
                showNotification({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente driverliste."
                });
            } finally {
                setLoadingDrivers(false);
            }
        };

        loadDrivers();
    }, [opened, eventId, scheduledDate, initial.scheduledDate]);

    useEffect(() => {
        if (!opened) return;

        setDriverId(initial.driverId != null ? String(initial.driverId) : null);
        setVehicleId(initial.vehicleId != null ? String(initial.vehicleId) : null);
        setStatusId(initial.statusTransportId != null ? String(initial.statusTransportId) : null);
        setNote(initial.plannerNote ?? "");
        setPickUpPlace(initial.pickUpPlace ?? "");
        setDropOffPlace(initial.dropOffPlace ?? "");

        const d = (initial.scheduledDate ?? "").slice(0, 10);
        setScheduledDate(d ? new Date(`${d}T12:00:00`) : null);

        const t = (initial.scheduledTime ?? "").trim();
        setScheduledTime(t ? t.slice(0, 5) : "");
    }, [opened, initial]);

    useEffect(() => {
        if (!opened) return;

        const loadStatuses = async () => {
            try {
                const data = await getTransportStatusOptions();
                setStatusOptions(data);
            } catch {
                showNotification({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente statusliste."
                });
            }
        };

        loadStatuses();
    }, [opened]);

    const driverData = useMemo(() => {
        const onShift = driverOptions.filter((d) => d.hasShiftOnSelectedDate);
        const offShift = driverOptions.filter((d) => !d.hasShiftOnSelectedDate);

        const data: { value: string; label: string; disabled?: boolean }[] = [];

        if (onShift.length > 0) {
            data.push({
                value: "__onshift_header__",
                label: "PÅ VAGT",
                disabled: true,
            });

            onShift.forEach((d) => {
                data.push({
                    value: String(d.personId),
                    label: d.name,
                });
            });
        }

        if (offShift.length > 0) {
            data.push({
                value: "__offshift_header__",
                label: "IKKE PÅ VAGT",
                disabled: true,
            });

            offShift.forEach((d) => {
                data.push({
                    value: String(d.personId),
                    label: d.name,
                });
            });
        }

        return data;
    }, [driverOptions]);

    const vehicleData = useMemo(
        () => vehicles.map((v) => ({ value: String(v.id), label: v.name + (v.plate ? ` (${v.plate})` : "") })),
        [vehicles]
    );

    const statusData = useMemo(
        () => statusOptions.map((s) => ({ value: String(s.value), label: s.text })),
        [statusOptions]
    );

    const pickupOptions = useMemo(() => {
        const values = Array.from(new Set([
            ...(pickUpPlace ? [pickUpPlace] : []),
            ...pickupSuggestions,
        ]));

        return values.map((name) => ({ value: name, label: name }));
    }, [pickupSuggestions, pickUpPlace]);

    const dropoffOptions = useMemo(() => {
        const values = Array.from(new Set([
            ...(dropOffPlace ? [dropOffPlace] : []),
            ...dropoffSuggestions,
        ]));

        return values.map((name) => ({ value: name, label: name }));
    }, [dropoffSuggestions, dropOffPlace]);

    const save = async () => {
        if (!bookingId) return;

        const initialDate = (initial.scheduledDate ?? "").slice(0, 10);
        const initialTime = (initial.scheduledTime ?? "").slice(0, 5);

        const d =
            scheduledDate ? scheduledDate.toISOString().slice(0, 10) : (initialDate || null);

        const t =
            scheduledTime ? `${scheduledTime}:00` : (initialTime ? `${initialTime}:00` : null);

        const dto = {
            scheduledDate: d,
            scheduledTime: t,

            driverId: driverId ? Number(driverId) : null,
            vehicleId: vehicleId ? Number(vehicleId) : null,
            statusTransportId: statusId ? Number(statusId) : null,
            note: note?.trim() ? note.trim() : "",

            pickUpPlace: isDeparture(initial.kind) ? (pickUpPlace?.trim() ? pickUpPlace.trim() : null) : null,
            dropOffPlace: isArrival(initial.kind) ? (dropOffPlace?.trim() ? dropOffPlace.trim() : null) : null,
        };

        try {
            setSaving(true);
            await updateRequestAdmin(bookingId, dto);
            showNotification({ color: "green", title: "Gemt", message: "Booking opdateret." });
            onSaved();
            onClose();
        } catch {
            showNotification({ color: "red", title: "Fejl", message: "Kunne ikke gemme booking." });
        } finally {
            setSaving(false);
        }


    };


    const isCancelledSelected = useMemo(() => {

        if (!statusId) return false;

        const selected = statusOptions.find((s) => String(s.value) === String(statusId));

        if (!selected) return false;
        return String(selected.text).toLowerCase().trim() === "cancel";
    }, [statusId, statusOptions]);


    const removeBooking = async () => {
        if (!bookingId) return;

        if (!isCancelledSelected) {
            showNotification({
                color: "yellow",
                title: "Kan ikke slettes",
                message: "Booking kan først slettes når status er sat til Cancelled."
            });
            return;
        }

        const ok = window.confirm(
            "Er du sikker på, at du vil slette denne booking permanent?"
        );

        if (!ok) return;

        try {
            setDeleting(true);
            await deleteRequestAdmin(bookingId);
            showNotification({
                color: "green",
                title: "Slettet",
                message: "Booking er blevet slettet."
            });
            onSaved();
            onClose();
        } catch {
            showNotification({
                color: "red",
                title: "Fejl",
                message: "Kunne ikke slette booking."
            });
        } finally {
            setDeleting(false);
        }
    };

    return (

        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size="md"
            title="Rediger booking"
            overlayProps={{ opacity: 0.25 }}
        >
            <Stack gap="md">
                <Text c="dimmed" size="sm">Ret dato/tid og planlægning.</Text>

                <Divider />

                <DateInput
                    label="Scheduled date"
                    placeholder="Vælg dato..."
                    value={scheduledDate}
                    onChange={setScheduledDate}
                />

                <TimeInput
                    label="Scheduled time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.currentTarget.value)}
                    withSeconds={false}
                />

                {locError && (
                    <Alert color="yellow" title="Lokationer">
                        {locError}
                    </Alert>
                )}

                {isDeparture(initial.kind) && (
                    <Select
                        label="Pick-up place"
                        placeholder={loadingLoc ? "Henter lokationer..." : "Vælg pick-up sted"}
                        data={pickupOptions}
                        value={pickUpPlace || null}
                        onChange={(value) => setPickUpPlace(value ?? "")}
                        searchable
                        nothingFoundMessage="Ingen steder fundet"
                    />
                )}

                {isArrival(initial.kind) && (
                    <Select
                        label="Drop-off place"
                        placeholder={loadingLoc ? "Henter lokationer..." : "Vælg drop-off sted"}
                        data={dropoffOptions}
                        value={dropOffPlace || null}
                        onChange={(value) => setDropOffPlace(value ?? "")}
                        searchable
                        nothingFoundMessage="Ingen steder fundet"
                    />
                )}

                <Divider />

                <Select
                    label="Driver"
                    placeholder={loadingDrivers ? "Henter drivers..." : "Vælg driver..."}
                    data={driverData}
                    value={driverId}
                    onChange={setDriverId}
                    searchable
                    clearable
                    nothingFoundMessage="Ingen drivers fundet"
                />

                <Select
                    label="Vehicle"
                    placeholder="Vælg bil..."
                    data={vehicleData}
                    value={vehicleId}
                    onChange={setVehicleId}
                    searchable
                    clearable
                />

                <Select
                    label="Status"
                    placeholder="Vælg status..."
                    data={statusData}
                    value={statusId}
                    onChange={setStatusId}
                    searchable
                    clearable
                />

                <Textarea
                    label="Note"
                    placeholder="Evt note..."
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    autosize
                    minRows={3}
                />

                <Group justify="space-between" mt="sm">
                    <Group>
                        <Button
                            variant="default"
                            leftSection={<IconX size={16} />}
                            onClick={onClose}
                        >
                            Luk
                        </Button>

                        {isCancelledSelected && (
                            <Button
                                color="red"
                                variant="light"
                                onClick={removeBooking}
                                loading={deleting}
                            >
                                Slet booking
                            </Button>
                        )}
                    </Group>

                    <Button
                        leftSection={<IconDeviceFloppy size={16} />}
                        onClick={save}
                        loading={saving}
                        style={{ backgroundColor: RED }}
                    >
                        Gem
                    </Button>
                </Group>
            </Stack>
        </Drawer>
    );
}